import React, { FC } from 'react';
import {
  BrandLogo,
  Button,
  colors,
  Icon,
  IconButton,
  Tooltip,
} from '@kontentino/ui';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import { faClose } from '@fortawesome/pro-regular-svg-icons/faClose';
import Link from 'components/shared/Link';
import { createSearchFromParams, routePathToUrl } from 'utils/url';
import routes, { modalRoutes } from 'constants/routes';
import { ListPost } from 'types/PostDetail';
import { ApiClientError } from 'api/client';
import { useMutation } from 'react-query';
import PostApi from 'api/post';
import { useToast } from 'app/hooks/useToast';
import Popup from 'utils/popup';
import UserPermissionGate from 'components/UserPermissionGate';
import { UserPermission } from 'constants/userPermission';
import { useTranslation } from 'react-i18next';
import {
  useInvalidatePostDetailQueries,
  useInvalidatePostsQueries,
} from 'app/modules/posts/hooks/useInvalidatePostsQueries';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';
import TranslationStatusBadge from 'app/modules/posts/modules/translation/TranslationStatusBadge';
import { usePostsLayoutState } from 'app/modules/posts/hooks/usePostsLayoutState';

type Props = {
  localPosts: ListPost[];
  globalPostId: number;
};

const PostOverviewLocalPostsList: FC<Props> = ({
  localPosts,
  globalPostId,
}) => {
  const toast = useToast();
  const { t } = useTranslation();
  const isNewPostExperienceActive = usePostsLayoutState(
    (state) => state.isNewPostExperienceActive,
  );
  const invalidatePosts = useInvalidatePostsQueries();
  const invalidatePostDetail = useInvalidatePostDetailQueries();

  const deletePost = useMutation(
    (postId: number) => PostApi.deletePost(postId),
    {
      onError: (e: ApiClientError) => {
        toast(
          e?.userMessage ?? t('globalPages.unableToDeletePostMessage'),
          'error',
        );
      },

      onSuccess: () => {
        invalidatePosts();
        invalidatePostDetail({
          postId: globalPostId,
        });
        toast(t('deletePostSuccessfullMessage'), 'info');
      },
    },
  );

  const handleDeleteClick = async (postId: number) => {
    const { value: confirmed } = await Popup.common({
      title: t('globalPages.deleteLocalPostTitle'),
      reverseButtons: true,
      showCancelButton: true,
      confirmButtonColor: colors.danger.default,
      confirmButtonText: t('delete'),
    });

    if (confirmed) {
      deletePost.mutate(postId);
    }
  };

  if (localPosts.length === 0) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-pb-4 tw-text-grayscale-100">
        {t('globalPages.youHaveNotAddedAnyLocalProfilesYet')}
      </div>
    );
  }

  return (
    <div className="tw-mb-2 tw-flex tw-flex-col tw-gap-y-2 tw-divide-y tw-divide-grayscale-20">
      {localPosts.map((post, index) => (
        <div
          key={index}
          className="tw-flex tw-items-start tw-justify-between tw-gap-x-2 tw-pt-2  tw-text-md first:tw-pt-0"
        >
          <div
            key={`local-posts-${post.id}-${index}`}
            className="tw-flex tw-items-center"
          >
            <BrandLogo
              src={post?.page.logo?.src}
              name={post.page.name}
              size={28}
              socialMediaType={getSocialMediaTypeForBrandLogo(post.page.type)}
              iconSize={getYtLogoGuidelineSize(8, post.page.type)}
            />
          </div>

          <div className="tw-flex tw-w-full tw-flex-col tw-overflow-hidden">
            <div className="tw-flex tw-w-full tw-justify-between tw-gap-1 ">
              <div className="tw-flex tw-min-w-0 tw-items-center tw-gap-2">
                <Tooltip content={post.page.name}>
                  <p className="tw-min-w-0 tw-truncate">{post.page.name}</p>
                </Tooltip>
                <UserPermissionGate scopes={UserPermission.POST_EDIT_TEXT}>
                  <div className="tw-flex tw-shrink-0">
                    <TranslationStatusBadge
                      postId={post.id}
                      pageType={post.page.type}
                      canEdit
                    />
                  </div>
                </UserPermissionGate>
              </div>

              <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-gap-x-2 tw-text-sm">
                <p className="tw-whitespace-nowrap tw-text-grayscale-100">
                  Post ID #{post.id}
                </p>
                <Link
                  target="_blank"
                  to={routePathToUrl({
                    pathname: routes.POSTS_CALENDAR,
                    hash: isNewPostExperienceActive
                      ? modalRoutes.POST_DETAIL_NEW
                      : modalRoutes.POST_DETAIL,
                    search: createSearchFromParams({
                      id: post.id,
                    }),
                  })}
                >
                  <Button
                    variant="ghost"
                    className="tw-h-auto !tw-p-0 tw-text-sm tw-text-primary-100"
                    data-cy="local-post_open-button"
                  >
                    {t('open')}
                  </Button>
                </Link>
                <UserPermissionGate
                  scopes={UserPermission.GLOBAL_PAGE_ASSIGN_CHANNEL}
                >
                  <IconButton
                    variant="ghost"
                    icon={<Icon icon={faClose} size={16} />}
                    className="tw-h-auto tw-w-auto !tw-p-0 !tw-text-sm tw-text-grayscale-100"
                    onClick={() => handleDeleteClick(post.id)}
                    data-cy="local-post_delete-button"
                  />
                </UserPermissionGate>
              </div>
            </div>

            {!!post.pending_patches_count && (
              <p className="tw-text-sm tw-font-regular tw-text-danger-100">
                {t('globalPages.pendingChangesToApply')}:{' '}
                {post.pending_patches_count}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default PostOverviewLocalPostsList;
